import React, { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import GlassCard from '../card/GlassCard';
import { themeColors } from 'helpers/utils';
import { defaultChartOptions } from 'services/glass-management/glassManagement';
import SLAChart from '../SLAChart';
import { useSelector, useDispatch } from 'react-redux';
import { openModal } from 'slices/glassDashboard/glassDataModalSlice';
import { otifCols } from 'services/glass-management/otif';
import dayjs from 'dayjs';
import { Col, Row } from 'react-bootstrap';

const TripleLamTough = () => {
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector(s => s.otif);
    const colours = {
        triple: themeColors.primary,
        lam: themeColors.warning,
        tough: themeColors.success
    }

    if (error)
        toast.error('Something went wrong!', error);

    const cols = otifCols.map(col => col.accessor == 'GlassRequiredDate' || col.accessor == 'IsOnTime' ? { ...col, isVisible: false } : col);

    const buildDataSets = useCallback((data = {}) => {
        const triples = [];
        const lams = [];
        const toughs = [];
        let totals = [];

        const raw = data?.raw;
        if (!raw || typeof raw !== 'object') return { triples, lams, toughs };

        for (const keyData of Object.values(raw)) {
            for (let i = 0, len = keyData.length; i < len; i++) {
                const item = keyData[i];
                const t = item && item.GlassType;
                switch (t) {
                    case 'Toughened':
                        toughs.push({
                            ...item,
                            ScannedDate: dayjs(item.ScannedDate).format('DD/MM/YYYY'),
                            OrderDate: dayjs(item.OrderDate).format('DD/MM/YYYY'),
                            RequiredDate: dayjs(item.RequiredDate).format('DD/MM/YYYY')
                        });
                        break;
                    case 'Triple':
                        triples.push({
                            ...item,
                            ScannedDate: dayjs(item.ScannedDate).format('DD/MM/YYYY'),
                            OrderDate: dayjs(item.OrderDate).format('DD/MM/YYYY'),
                            RequiredDate: dayjs(item.RequiredDate).format('DD/MM/YYYY')
                        });
                        break;
                    case 'Laminated':
                        lams.push({
                            ...item,
                            ScannedDate: dayjs(item.ScannedDate).format('DD/MM/YYYY'),
                            OrderDate: dayjs(item.OrderDate).format('DD/MM/YYYY'),
                            RequiredDate: dayjs(item.RequiredDate).format('DD/MM/YYYY')
                        });
                        break;
                    default:
                        break;
                }
            }
        }
        totals = [...triples, ...lams, ...toughs];
        return {
            triples,
            lams,
            toughs,
            totals,
            xAxis: [...new Set(totals.map(x => x.ScannedDate))]
        };
    }, [data]);
    const { triples, lams, toughs, xAxis, totals } = buildDataSets(data);

    const getChartData = useCallback((data, keys) => {
        return keys?.map(key => {
            return {
                name: key,
                value: data?.filter(x => x.ScannedDate == key)?.length || 0
            }
        })
    }, [data]);

    const allDataClick = useCallback(() => {
        dispatch(openModal({
            modalData: totals,
            modalLoading: false,
            modalShow: true,
            modalTitle: 'All Toughened, Laminate and Triple Glazed',
            tableCols: cols,
        }))
    }, [totals]);

    const getBarOptions = useCallback(() => {
        return {
            ...defaultChartOptions,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                }
            },
            xAxis: {
                type: 'category',
                data: xAxis,
            },
            legend: { show: true },
            series: [
                {
                    type: 'bar',
                    stack: 'Total',
                    name: 'Triple Glazed',
                    color: [colours.triple],
                    data: getChartData(triples, xAxis)
                },
                {
                    type: 'bar',
                    stack: 'Total',
                    name: 'Laminate',
                    color: [colours.lam],
                    data: getChartData(lams, xAxis),
                },
                {
                    type: 'bar',
                    stack: 'Total',
                    name: 'Toughened',
                    color: [colours.tough],
                    data: getChartData(toughs, xAxis),
                },
            ],
        }
    }, [data]);

    // const pieData = [
    //     {
    //         name: 'Triple Glazed',
    //         value: triples?.length,
    //         itemStyle: { color: colours.triple },
    //     },
    //     {
    //         name: 'Laminate',
    //         value: lams?.length,
    //         itemStyle: { color: colours.lam },
    //     },
    //     {
    //         name: 'Toughened',
    //         value: toughs?.length,
    //         itemStyle: { color: colours.tough },
    //     }
    // ];
    // const getPieOptions = useCallback(() => {
    //     return {
    //         tooltip: {
    //             trigger: 'item'
    //         },
    //         series: [
    //             {
    //                 name: 'Glass Type',
    //                 type: 'pie',
    //                 legend: { show: true },
    //                 roseType: 'area',
    //                 avoidLabelOverlap: false,
    //                 center: ['50%', '50%'],
    //                 padAngle: 5,
    //                 itemStyle: {
    //                     borderRadius: 10
    //                 },
    //                 label: {
    //                     formatter: '{c}'
    //                 },
    //                 labelLine: {
    //                     show: false
    //                 },
    //                 data: pieData
    //             }
    //         ]
    //     }
    // }, [data]);

    const handleChartClick = useCallback(e => {
        if (!e || e.componentType !== 'series') return;

        const key = e?.name;
        const type = e?.componentSubType;
        const name = type === 'pie' ? e.name : e?.seriesName;

        let list = [];
        switch (name) {
            case 'Laminate':
                list = lams;
                break;
            case 'Triple Glazed':
                list = triples;
                break;
            case 'Toughened':
                list = toughs;
                break;
            default:
                break;
        }

        if (type === 'bar') list = list.filter(x => x.ScannedDate == key);

        if (!list || list.length === 0) return;

        dispatch(openModal({
            modalData: list,
            modalLoading: false,
            modalShow: true,
            modalTitle: `${name} - ${key}`,
            tableCols: cols,
        }));
    }, [lams, triples, toughs, dispatch]);

    return (
        <>
            <GlassCard
                title={`Triple Glazed: ${triples?.length}, Laminates: ${lams?.length}, Toughened: ${toughs?.length}`}
                colour='secondary'
                heroValue={{ func: () => allDataClick() }}>
                <Row className='h-100'>
                    <Col xs={12} className='h-100'>
                        <SLAChart
                            loading={loading}
                            options={getBarOptions()}
                            onClick={handleChartClick} />
                    </Col>
                    {/* <Col xs={6} className='h-100'>
                        <SLAChart
                            loading={loading}
                            options={getPieOptions()}
                            onClick={handleChartClick} />
                    </Col> */}
                </Row>
            </GlassCard>
        </>
    );
}

export default memo(TripleLamTough);